import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const STATUS_CONFIG = {
  success: {
    label: 'Ödeme Onaylandı',
    color: 'bg-green-100 text-green-700 border-green-200',
    dot: 'bg-green-500',
    icon: 'CheckCircle',
  },
  failed: {
    label: 'Ödeme Başarısız',
    color: 'bg-red-100 text-red-700 border-red-200',
    dot: 'bg-red-500',
    icon: 'XCircle',
  },
  pending: {
    label: 'Beklemede',
    color: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    dot: 'bg-yellow-500',
    icon: 'Clock',
  },
};

const MAX_EVENTS = 50;

const formatAmount = (amount) => {
  if (!amount) return '-';
  const num = parseFloat(amount);
  if (isNaN(num)) return amount;
  return `₺${(num / 100)?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`;
};

const formatTime = (ts) => {
  if (!ts) return '-';
  return new Date(ts)?.toLocaleString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const EventRow = ({ event, isNew }) => {
  const cfg = STATUS_CONFIG?.[event?.status] || STATUS_CONFIG?.pending;
  return (
    <div
      className={`flex items-start gap-3 px-4 py-3 border-b border-border last:border-0 transition-all duration-500 ${
        isNew ? 'bg-primary/5 animate-pulse-once' : 'hover:bg-muted/30'
      }`}
    >
      {/* Status dot */}
      <div className="flex-shrink-0 mt-1">
        <span className={`inline-block w-2.5 h-2.5 rounded-full ${cfg?.dot}`} />
      </div>
      {/* Main info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${cfg?.color}`}
          >
            <Icon name={cfg?.icon} size={11} />
            {cfg?.label}
          </span>
          <span className="text-xs font-mono text-muted-foreground truncate max-w-[180px]">
            {event?.merchant_oid || '-'}
          </span>
          {event?.test_mode && (
            <span className="px-1.5 py-0.5 rounded text-xs bg-purple-100 text-purple-700 border border-purple-200 font-medium">
              TEST
            </span>
          )}
        </div>

        <div className="flex items-center gap-3 mt-1 flex-wrap">
          <span className="text-sm font-semibold text-foreground">
            {formatAmount(event?.total_amount)}
          </span>
          {event?.currency && event?.currency !== 'TL' && (
            <span className="text-xs text-muted-foreground">{event?.currency}</span>
          )}
          {event?.payment_type && (
            <span className="text-xs text-muted-foreground capitalize">{event?.payment_type}</span>
          )}
          {event?.failed_reason_msg && (
            <span className="text-xs text-red-600 truncate max-w-[200px]" title={event?.failed_reason_msg}>
              {event?.failed_reason_msg}
            </span>
          )}
        </div>
      </div>
      {/* Timestamp */}
      <div className="flex-shrink-0 text-right">
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {formatTime(event?.created_at)}
        </span>
        {!event?.hash_valid && (
          <div className="flex items-center gap-1 justify-end mt-0.5">
            <Icon name="AlertTriangle" size={10} className="text-orange-500" />
            <span className="text-xs text-orange-600">Hash geçersiz</span>
          </div>
        )}
      </div>
    </div>
  );
};

const PayTRWebhookTracker = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [newEventIds, setNewEventIds] = useState(new Set());
  const [stats, setStats] = useState({ success: 0, failed: 0, total: 0 });
  const [missedCount, setMissedCount] = useState(0);
  const channelRef = useRef(null);
  const listRef = useRef(null);

  // Load recent events
  const loadEvents = async () => {
    try {
      const { data, error } = await supabase?.from('paytr_webhook_events')?.select('*')?.order('created_at', { ascending: false })?.limit(MAX_EVENTS);

      if (error) {
        if (error?.code?.startsWith('42')) throw error;
        console.warn('PayTR events load error:', error?.message);
        return;
      }

      const evts = data || [];
      setEvents(evts);
      computeStats(evts);
    } catch (err) {
      console.error('PayTR tracker load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const computeStats = (evts) => {
    const success = evts?.filter((e) => e?.status === 'success')?.length;
    const failed = evts?.filter((e) => e?.status === 'failed')?.length;
    // Missed = failed events where hash_valid is true (legit PayTR callbacks that failed)
    const missed = evts?.filter((e) => e?.status === 'failed' && e?.hash_valid)?.length;
    setStats({ success, failed, total: evts?.length });
    setMissedCount(missed);
  };

  // Flash new event highlight
  const flashNew = (id) => {
    setNewEventIds((prev) => new Set([...prev, id]));
    setTimeout(() => {
      setNewEventIds((prev) => {
        const next = new Set(prev);
        next?.delete(id);
        return next;
      });
    }, 3000);
  };

  // Subscribe to realtime
  useEffect(() => {
    loadEvents();

    const channel = supabase?.channel('paytr_webhook_realtime')?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'paytr_webhook_events',
        },
        (payload) => {
          const newEvent = payload?.new;
          if (!newEvent) return;

          setEvents((prev) => {
            const updated = [newEvent, ...prev]?.slice(0, MAX_EVENTS);
            computeStats(updated);
            return updated;
          });

          flashNew(newEvent?.id);

          // Scroll to top on new event
          if (listRef?.current) {
            listRef.current.scrollTop = 0;
          }
        }
      )?.subscribe((status) => {
        setConnected(status === 'SUBSCRIBED');
      });

    channelRef.current = channel;

    return () => {
      if (channelRef?.current) {
        supabase?.removeChannel(channelRef?.current);
      }
    };
  }, []);

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
            <Icon name="Zap" size={18} className="text-blue-600" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-foreground">PayTR Gerçek Zamanlı Webhook</h2>
            <p className="text-xs text-muted-foreground">Ödeme onayları ve durum değişiklikleri</p>
          </div>
        </div>

        {/* Connection indicator */}
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${
              connected
                ? 'bg-green-50 text-green-700 border-green-200' :'bg-gray-50 text-gray-500 border-gray-200'
            }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${
                connected ? 'bg-green-500 animate-pulse' : 'bg-gray-400'
              }`}
            />
            {connected ? 'Canlı' : 'Bağlanıyor...'}
          </span>
        </div>
      </div>
      {/* Stats row */}
      <div className="grid grid-cols-4 divide-x divide-border border-b border-border">
        <div className="px-4 py-3 text-center">
          <p className="text-lg font-bold text-foreground">{stats?.total}</p>
          <p className="text-xs text-muted-foreground">Toplam</p>
        </div>
        <div className="px-4 py-3 text-center">
          <p className="text-lg font-bold text-green-600">{stats?.success}</p>
          <p className="text-xs text-muted-foreground">Onaylı</p>
        </div>
        <div className="px-4 py-3 text-center">
          <p className="text-lg font-bold text-red-600">{stats?.failed}</p>
          <p className="text-xs text-muted-foreground">Başarısız</p>
        </div>
        <div className="px-4 py-3 text-center">
          <p className="text-lg font-bold text-orange-600">{missedCount}</p>
          <p className="text-xs text-muted-foreground">Kaçırılan</p>
        </div>
      </div>
      {/* Event list */}
      <div ref={listRef} className="max-h-80 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <div className="w-6 h-6 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
          </div>
        ) : events?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center px-4">
            <Icon name="Radio" size={32} className="text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">Henüz webhook eventi yok</p>
            <p className="text-xs text-muted-foreground mt-1">
              PayTR'dan gelen callback'ler burada gerçek zamanlı görünecek
            </p>
          </div>
        ) : (
          events?.map((event) => (
            <EventRow
              key={event?.id}
              event={event}
              isNew={newEventIds?.has(event?.id)}
            />
          ))
        )}
      </div>
      {/* Missed callbacks warning */}
      {missedCount > 0 && (
        <div className="px-4 py-3 border-t border-border bg-orange-50">
          <div className="flex items-center gap-2">
            <Icon name="AlertTriangle" size={14} className="text-orange-600 flex-shrink-0" />
            <p className="text-xs text-orange-700">
              <span className="font-semibold">{missedCount} kaçırılan/başarısız callback</span> tespit edildi.
              Bu ödemeler PayTR tarafından doğrulandı ancak sistemde işlenemedi.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayTRWebhookTracker;
